import { Icon } from "@iconify/react/dist/iconify.js";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { Link } from "react-router-dom";
import { useSentiment } from "../../contexts/SentimentContext";
import { useCallData } from "../../contexts/CallDataContext";
import { useNavigate } from "react-router-dom";
import { useCustomer } from "../../contexts/CustomerContext";
import TicketCard from "../../components/tickets/ticketCard";
import { useTickets } from "../../contexts/TicketContext.jsx";

import CallChart from "../../components/insights/CallChart";
import DurationChart from "../../components/insights/DurationChart";
import SuccessChart from "../../components/insights/SuccessChart";

import { useAuth0 } from "../../components/auth/DevAuth0Provider";

import { useState, useEffect } from "react";

import CustomerImpersonation from "../../components/admin/CustomerImpersonation";
import { useWeek } from "../../contexts/WeekContext";
import { startOfDay, endOfDay, isWithinInterval } from "date-fns";
import { parseCustomTimestamp } from "../../utils/dateUtils";
import useImpersonationRedirect from "../../hooks/useImpersonationRedirect";

const AdminDashboard = () => {
    const user = useAuthUser();
    const { sentimentCounts } = useSentiment();
    const { data: callData } = useCallData();
    const { dateRange } = useWeek();
    const navigate = useNavigate();
    const {
        availableCustomers,
        currentCustomer,
        isImpersonating,
        isLoading: customersLoading,
        error: customersError
    } = useCustomer();

    // Set up auto-redirect when impersonation ends
    useImpersonationRedirect();

    const { tickets, isLoading: isLoadingTickets, error: ticketsError } = useTickets();

    // Animation states
    const [animationStates, setAnimationStates] = useState({
        header: false,
        insights: false,
        alerts: false,
        tokens: false
    });

    const { getAccessTokenSilently } = useAuth0();

    // Calculate token metrics
    const [tokenMetrics, setTokenMetrics] = useState({
        llm: 0,
        stt: 0,
        calling: 0,
        tts: 0,
        llmCallCount: 0,
        sttCallCount: 0,
        callingCallCount: 0,
        ttsCallCount: 0
    });

    useEffect(() => {
        if (!callData || callData.length === 0) {
            setTokenMetrics({ 
                llm: 0, 
                stt: 0, 
                calling: 0, 
                tts: 0,
                llmCallCount: 0,
                sttCallCount: 0,
                callingCallCount: 0,
                ttsCallCount: 0
            });
            return;
        }
        if (!dateRange?.start || !dateRange?.end) {
            setTokenMetrics({ 
                llm: 0, 
                stt: 0, 
                calling: 0, 
                tts: 0,
                llmCallCount: 0,
                sttCallCount: 0,
                callingCallCount: 0,
                ttsCallCount: 0
            });
            return;
        }

        const metrics = {
            llm: 0,
            stt: 0,
            calling: 0,
            tts: 0,
            llmCallCount: 0,
            sttCallCount: 0,
            callingCallCount: 0,
            ttsCallCount: 0
        };

        const rangeStart = startOfDay(dateRange.start);
        const rangeEnd = endOfDay(dateRange.end);

        callData.forEach((call) => {
            // First check if the call is within the date range
            const callDate = parseCustomTimestamp(call?.metadata?.timestamp);
            if (!callDate || !isWithinInterval(callDate, { start: rangeStart, end: rangeEnd })) {
                return; // Skip calls outside the date range
            }

            // Check for LLM token data
            if (call.token_usage?.total_tokens) {
                metrics.llm += call.token_usage.total_tokens;
                metrics.llmCallCount += 1;
            }
            
            // Placeholder for other metrics (STT, Calling, TTS)
            // These will be implemented when the data becomes available
            // if (call.analytics?.stt_tokens) {
            //     metrics.stt += call.analytics.stt_tokens;
            //     metrics.sttCallCount += 1;
            // }
            // if (call.analytics?.calling_tokens) {
            //     metrics.calling += call.analytics.calling_tokens;
            //     metrics.callingCallCount += 1;
            // }
            // if (call.analytics?.tts_tokens) {
            //     metrics.tts += call.analytics.tts_tokens;
            //     metrics.ttsCallCount += 1;
            // }
        });

        setTokenMetrics(metrics);
    }, [callData, dateRange]);


    // Trigger staggered animations on component mount
    useEffect(() => {
        const timeouts = [];

        // Stagger the animations with delays
        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, header: true }));
        }, 100));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, insights: true }));
        }, 300));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, alerts: true }));
        }, 500));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, tokens: true }));
        }, 700));

        // Cleanup timeouts on unmount
        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, []);


    return (
        <div className="responsive-page-layout scrollable">
            <div className={`home-title dashboard-header-responsive transition-all duration-700 ease-out-in ${
                animationStates.header
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 -translate-y-4'
            }`}>
                <div className="flex flex-col gap-2 items-start w-full lg:w-auto">
                    <h4 className="text-xl sm:text-2xl flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                        <div className="flex flex-row gap-1 items-center">
                            <Icon icon="mdi:hand-wave" className="text-primary text-2xl"></Icon>
                            Hello <span className="text-secondary font-bold">{user.name}</span>
                        </div>
                        {isImpersonating && (
                            <span className="text-font-primary text-sm p-1.5 sm:p-2 border-2 border-secondary-50 rounded-md">
                                Viewing as <span className="font-bold text-secondary">{currentCustomer?.name}</span>
                            </span>
                        )}
                    </h4>
                    <p className="text-font-secondary text-sm sm:text-base lg:text-lg">
                        {isImpersonating
                            ? `Here's a snapshot of ${currentCustomer?.name}'s AI assistant performance this week.`
                            : "Here's a quick snapshot of all customer AI assistants' performance this week."
                        }
                    </p>
                </div>
                <div className="header-right-3">
                    <div className='flex flex-col sm:flex-row gap-2 w-full lg:w-fit'>
                        <div className="flex flex-row w-fit gap-1 items-center text-xs sm:text-sm py-2 px-3 bg-bgSecondary border border-border rounded-md h-fit">
                            <Icon icon="mdi:database" className="text-lg sm:text-xl"></Icon>
                            <p>Customers: <span className="font-bold">{availableCustomers.length}</span></p>
                        </div>
                    </div>
                </div>
            </div>
            {/* Token Metrics Summary */}
            <div className={`home-insights w-full flex flex-col gap-4 transition-all duration-1000 ease-out-in ${
                animationStates.tokens
                    ? 'opacity-100'
                    : 'opacity-0'
            }`}>
                <div className="home-insights-title responsive-flex-left">
                    <div className="flex flex-row w-fit p-0.5 border-b-2 border-primary items-center text-sm sm:text-base font-black uppercase">
                        {/* <Icon icon="material-symbols:token-outline" className="text-secondary responsive-icon-sm"></Icon> */}
                        Token Usage Summary
                    </div>
                    <Link to="/app/token-metrics" id="token-metrics-button" className="flex flex-row items-center gap-1 text-accent text-sm sm:text-base">
                        View all <Icon icon="mingcute:right-line" className="text-lg sm:text-xl"></Icon>
                    </Link>
                </div>
                <div className="dashboard-token-metrics-responsive">
                    <div className="bg-bgSecondary border border-border rounded-md flex-1 px-2 py-2 sm:px-3 flex justify-between items-center">
                        <div className="flex items-center gap-1">
                            <Icon icon="material-symbols:psychology-outline" className="text-lg sm:text-xl text-blue-500" />
                            <span className="text-sm sm:text-base font-semibold">LLM</span>
                        </div>
                        <span className="text-blue-500 font-bold text-lg sm:text-xl">
                            {tokenMetrics.llm.toLocaleString()}
                            {tokenMetrics.llmCallCount > 0 && (
                                <span className="text-sm font-normal ml-1 text-font-primary">
                                    ({Math.round(tokenMetrics.llm / tokenMetrics.llmCallCount)}/call)
                                </span>
                            )}
                        </span>
                    </div>
                    <div className="bg-bgSecondary border border-border rounded-md flex-1 px-2 py-1 sm:px-3 sm:py-2 flex justify-between items-center">
                        <div className="flex items-center gap-1">
                            <Icon icon="material-symbols:mic-outline" className="text-lg sm:text-xl text-orange-500" />
                            <span className="text-sm sm:text-base font-semibold">STT</span>
                        </div>
                        <span className="text-orange-500 font-bold text-lg sm:text-xl">
                            {tokenMetrics.stt.toLocaleString() || 'N/A'}
                            {tokenMetrics.sttCallCount > 0 && (
                                <span className="text-sm font-normal ml-1 text-font-primary">
                                    ({Math.round(tokenMetrics.stt / tokenMetrics.sttCallCount)}/call)
                                </span>
                            )}
                        </span>
                    </div>
                    <div className="bg-bgSecondary border border-border rounded-md flex-1 px-2 py-1 sm:px-3 sm:py-2 flex justify-between items-center">
                        <div className="flex items-center gap-1">
                            <Icon icon="material-symbols:call-outline" className="text-lg sm:text-xl text-purple-500" />
                            <span className="text-sm sm:text-base font-semibold">Calling</span>
                        </div>
                        <span className="text-purple-500 font-bold text-lg sm:text-xl">
                            {tokenMetrics.calling.toLocaleString() || 'N/A'}
                            {tokenMetrics.callingCallCount > 0 && (
                                <span className="text-sm font-normal ml-1 text-font-primary">
                                    ({Math.round(tokenMetrics.calling / tokenMetrics.callingCallCount)}/call)
                                </span>
                            )}
                        </span>
                    </div>
                    <div className="bg-bgSecondary border border-border rounded-md flex-1 px-2 py-1 sm:px-3 sm:py-2 flex justify-between items-center">
                        <div className="flex items-center gap-1">
                            <Icon icon="material-symbols:volume-up-outline" className="text-lg sm:text-xl text-green-500" />
                            <span className="text-sm sm:text-base font-semibold">TTS</span>
                        </div>
                        <span className="text-green-500 font-bold text-lg sm:text-xl">
                            {tokenMetrics.tts.toLocaleString() || 'N/A'}
                            {tokenMetrics.ttsCallCount > 0 && (
                                <span className="text-sm font-normal ml-1 text-font-primary">
                                    ({Math.round(tokenMetrics.tts / tokenMetrics.ttsCallCount)}/call)
                                </span>
                            )}
                        </span>
                    </div>
                </div>
            </div>
            {/* Key Insights */}
            <div className={`home-insights w-full flex flex-col gap-4 transition-all duration-1000 ease-out-in ${
                animationStates.insights
                    ? 'opacity-100'
                    : 'opacity-0'
            }`}>
                <div className="home-insights-title responsive-flex-left">
                    <div className="flex flex-row w-fit p-0.5 border-b-2 border-primary items-center text-sm sm:text-base font-black uppercase">
                        {/* <Icon icon="system-uicons:graph-bar" className="text-secondary responsive-icon-sm"></Icon> */}
                        Key Insights
                    </div>
                    <Link to={`/app/${currentCustomer?.id}/insights`} id="key-insights-button" className="flex flex-row items-center gap-1 text-accent text-sm sm:text-base">
                        View all <Icon icon="mingcute:right-line" className="text-lg sm:text-xl"></Icon>
                    </Link>
                </div>
                <div className="dashboard-insights-responsive">
                    <div className="chart-container-responsive">
                        <CallChart />
                    </div>
                    <div className="chart-container-responsive">
                        <DurationChart />
                    </div>
                    <div className="chart-container-responsive">
                        <SuccessChart />
                    </div>
                </div>
            </div>
            {/* Open Tickets */}
            <div className={`home-insights w-full flex flex-col gap-4 transition-all duration-1000 ease-out-in ${
                animationStates.alerts
                    ? 'opacity-100'
                    : 'opacity-0'
            }`}>
                <div className="flex flex-row w-fit p-0.5 border-b-2 border-primary items-center text-sm sm:text-base font-black uppercase">
                    {/* <Icon icon="f7:tickets" className="text-secondary responsive-icon-sm"></Icon> */}
                    Open Tickets
                </div>
                <div className="dashboard-alerts-responsive h-full">
                    {isLoadingTickets && <p className="text-gray-400 col-span-full">Loading tickets...</p> /* Loading indicator */}
                    {ticketsError && <p className="text-yellow-500 col-span-full">⚠️ {ticketsError}</p> /* Error message */}
                    {!isLoadingTickets && !ticketsError && tickets.length === 0 && (
                        <div className="flex flex-col items-center justify-center w-full text-center text-gray-400">
                            <Icon icon="carbon:no-ticket" className="text-6xl mb-4" />
                            <p className="text-lg">No tickets found</p>
                        </div>
                    )}
                    {!isLoadingTickets && !ticketsError && tickets.length > 0 && (() => {
                        const openTickets = tickets.filter(ticket => ticket.status === 'Open');
                        if (openTickets.length === 0) {
                            return (
                                <div className="flex flex-col items-center justify-center w-full h-full text-center text-gray-400">
                                    <Icon icon="carbon:no-ticket" className="text-6xl mb-4" />
                                    <p className="text-lg">No open tickets</p>
                                </div>
                            );
                        }
                        return openTickets.slice(0, 4).map(ticket => (
                            <TicketCard ticket={ticket} key={ticket.id || ticket.ticketNumber} />
                        ));
                    })()}
                </div>
            </div>
        </div>
    );
  };
  
export default AdminDashboard;
